import { Component, OnInit, inject, signal } from '@angular/core';
import { DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { ApiService } from '../../core/services/api.service';
import {
  PointTransactionRow,
  PointsTotals,
  UserPointsRow,
} from '../../core/models/api.models';

@Component({
  selector: 'app-points',
  standalone: true,
  imports: [DatePipe, FormsModule],
  templateUrl: './points.component.html',
  styleUrl: './points.component.scss',
})
export class PointsComponent implements OnInit {
  private readonly api = inject(ApiService);
  private readonly router = inject(Router);

  readonly tab = signal<'users' | 'history'>('users');
  readonly loading = signal(false);
  readonly error = signal<string | null>(null);
  readonly success = signal<string | null>(null);

  readonly totals = signal<PointsTotals | null>(null);
  readonly userRows = signal<UserPointsRow[]>([]);
  readonly userPage = signal(1);
  readonly userTotal = signal(0);
  readonly userLimit = 20;
  searchQ = '';

  readonly historyRows = signal<PointTransactionRow[]>([]);
  readonly historyPage = signal(1);
  readonly historyTotal = signal(0);
  readonly historyLimit = 20;
  historyUserId = '';
  historyType = '';

  readonly adjusting = signal(false);
  readonly adjustUser = signal<UserPointsRow | null>(null);
  adjustAmount = 100;
  adjustDirection: 'credit' | 'debit' = 'credit';
  adjustReason = '';

  ngOnInit(): void {
    this.loadUsers();
  }

  setTab(tab: 'users' | 'history'): void {
    this.tab.set(tab);
    this.error.set(null);
    if (tab === 'users') this.loadUsers();
    else this.loadHistory();
  }

  loadUsers(page = 1): void {
    this.loading.set(true);
    this.error.set(null);
    this.userPage.set(page);
    this.api.getUserPoints(page, this.userLimit, this.searchQ).subscribe({
      next: (data) => {
        this.totals.set(data.totals || null);
        this.userRows.set(data.rows || []);
        this.userTotal.set(data.total || 0);
        this.loading.set(false);
      },
      error: (err: { message?: string }) => {
        this.error.set(err?.message || 'Failed to load user points');
        this.loading.set(false);
      },
    });
  }

  searchUsers(): void {
    this.loadUsers(1);
  }

  loadHistory(page = 1): void {
    this.loading.set(true);
    this.error.set(null);
    this.historyPage.set(page);
    this.api
      .getPointsHistory(page, this.historyLimit, {
        user_id: this.historyUserId || undefined,
        type: this.historyType || undefined,
      })
      .subscribe({
        next: (data) => {
          this.historyRows.set(data.rows || []);
          this.historyTotal.set(data.total || 0);
          this.loading.set(false);
        },
        error: (err: { message?: string }) => {
          this.error.set(err?.message || 'Failed to load points history');
          this.loading.set(false);
        },
      });
  }

  filterHistory(): void {
    this.loadHistory(1);
  }

  fullName(u: UserPointsRow | { first_name?: string | null; last_name?: string | null } | null | undefined): string {
    if (!u) return '—';
    return `${u.first_name || ''} ${u.last_name || ''}`.trim() || '—';
  }

  userPages(): number {
    return Math.max(1, Math.ceil(this.userTotal() / this.userLimit));
  }

  historyPages(): number {
    return Math.max(1, Math.ceil(this.historyTotal() / this.historyLimit));
  }

  openUser(id: number): void {
    void this.router.navigate(['/users', id]);
  }

  n(v: number | string | null | undefined): number {
    return parseInt(String(v ?? 0), 10) || 0;
  }

  openAdjust(row: UserPointsRow, event: Event): void {
    event.stopPropagation();
    this.adjustUser.set(row);
    this.adjustAmount = 100;
    this.adjustDirection = 'credit';
    this.adjustReason = '';
    this.error.set(null);
    this.success.set(null);
  }

  cancelAdjust(): void {
    this.adjustUser.set(null);
    this.adjustReason = '';
  }

  signedPoints(): number {
    const amount = Math.abs(parseInt(String(this.adjustAmount || 0), 10) || 0);
    if (amount < 1) return 0;
    return this.adjustDirection === 'debit' ? -amount : amount;
  }

  confirmAdjust(): void {
    const row = this.adjustUser();
    const points = this.signedPoints();
    const reason = this.adjustReason.trim();
    if (!row || this.adjusting() || points === 0 || !reason) return;

    this.adjusting.set(true);
    this.error.set(null);
    this.success.set(null);
    this.api.adjustUserPoints({ user_id: row.id, points, reason }).subscribe({
      next: (data) => {
        this.adjusting.set(false);
        this.adjustUser.set(null);
        const verb = points > 0 ? 'credited' : 'debited';
        this.success.set(
          `${Math.abs(points)} points ${verb} for user #${row.id}. Balance now ${data.points_balance}.`
        );
        this.loadUsers(this.userPage());
      },
      error: (err: { message?: string }) => {
        this.adjusting.set(false);
        this.error.set(err?.message || 'Failed to adjust points');
      },
    });
  }
}
